import React, { useState } from 'react';
import { MailIcon, MapPinIcon, SendIcon } from 'lucide-react';
import { usePageSEO } from '../hooks/usePageSEO';
export function ContactPage() {
  usePageSEO({
    title: 'Contact | Design Flow Digitals',
    description: 'Get in touch with Design Flow Digitals to discuss your Shopify project. Custom store development, theme design, and app solutions for premium ecommerce brands.'
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    // Simulate API call
    setTimeout(() => setFormStatus('success'), 1500);
  };
  return <div className="pt-32 pb-20" data-id="element-531">
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-20" data-id="element-532">
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-533">Contact Us</h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-534">
          Have a project in mind? We'd love to hear about it.
        </p>
      </section>

      <section className="section-padding pt-0" data-id="element-535">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto" data-id="element-536">
          {/* Contact Info */}
          <div className="flex flex-col gap-12" data-id="element-537">
            <div className="glass-card p-10 border-white/10" data-id="element-538">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6" data-id="element-539">
                <MailIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-540" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-541">Email</h3>
              <p className="text-gray-400 mb-4" data-id="element-542">
                Our friendly team is here to help.
              </p>
              <a href="mailto:support@designflowdigitals.com" className="text-xl font-medium text-gradient-gold hover:opacity-80 transition-opacity" data-id="element-543">
                support@designflowdigitals.com
              </a>
            </div>

            <div className="glass-card p-10 border-white/10" data-id="element-544">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6" data-id="element-545">
                <MapPinIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-546" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-547">Address</h3>
              <p className="text-gray-400 mb-4" data-id="element-548">
                Come say hello at our office HQ.
              </p>
              <address className="text-lg text-white not-italic leading-relaxed" data-id="element-549">
                Design Flow Digitals
                <br data-id="element-550" />
                Pankaj Maurya LLC
                <br data-id="element-551" />
                67 Wigley Loop
                <br data-id="element-552" />
                Whitwell, TN, 37397
                <br data-id="element-553" />
                United States
              </address>
            </div>
          </div>

          {/* Contact Form */}
          <div className="glass-card p-10 md:p-12 border-white/10" data-id="element-554">
            <h3 className="text-2xl font-bold text-white mb-8" data-id="element-555">
              Send us a message
            </h3>

            {formStatus === 'success' ? <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-8 text-center" data-id="element-556">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-557">
                  <SendIcon className="w-8 h-8 text-green-500" data-id="element-558" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-559">
                  Message Sent!
                </h4>
                <p className="text-gray-400" data-id="element-560">
                  We'll get back to you as soon as possible.
                </p>
                <button onClick={() => setFormStatus('idle')} className="mt-6 text-[#D4AF37] hover:underline" data-id="element-561">
                  Send another message
                </button>
              </div> : <form onSubmit={handleSubmit} className="flex flex-col gap-6" data-id="element-562">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-563">
                  <div className="flex flex-col gap-2" data-id="element-564">
                    <label htmlFor="name" className="text-sm font-medium text-gray-300" data-id="element-565">
                      Name
                    </label>
                    <input type="text" id="name" required className="bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors" placeholder="John Doe" data-id="element-566" />
                  </div>
                  <div className="flex flex-col gap-2" data-id="element-567">
                    <label htmlFor="email" className="text-sm font-medium text-gray-300" data-id="element-568">
                      Email
                    </label>
                    <input type="email" id="email" required className="bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors" placeholder="john@example.com" data-id="element-569" />
                  </div>
                </div>

                <div className="flex flex-col gap-2" data-id="element-570">
                  <label htmlFor="subject" className="text-sm font-medium text-gray-300" data-id="element-571">
                    Subject
                  </label>
                  <input type="text" id="subject" required className="bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors" placeholder="How can we help?" data-id="element-572" />
                </div>

                <div className="flex flex-col gap-2" data-id="element-573">
                  <label htmlFor="message" className="text-sm font-medium text-gray-300" data-id="element-574">
                    Message
                  </label>
                  <textarea id="message" rows={5} required className="bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors resize-none" placeholder="Tell us about your project..." data-id="element-575" />
                </div>

                <button type="submit" disabled={formStatus === 'submitting'} className="bg-gradient-gold text-black px-8 py-4 rounded-lg font-bold text-lg gold-glow-hover transition-all duration-300 mt-4 disabled:opacity-70 flex items-center justify-center gap-2" data-id="element-576">
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>}
          </div>
        </div>
      </section>
    </div>;
}